/**
 * Block 14 «Раскрой» — PR #24-B (EP-5). Сбор НОВЫХ годных остатков из результата
 * раскроя (`OptimizationResult`) для сохранения в склад (вкладка «Остатки»).
 *
 * Замыкает цикл остатков: PR #24-A — склад + per-project выбор «участвуют»;
 * здесь — после раскроя одной кнопкой кладём обрезки текущего раскроя обратно в
 * склад для будущих заказов.
 *
 * Чистая функция (без сети/DOM/стора) — юнит-тестируема. Решения STOP-pre (Aibek):
 *   §3.1 — берём годные `freeRects` ТОЛЬКО с ЦЕЛЫХ листов (листы-остатки — уже
 *          инвентарь, не «новые» обрезки). Это `whole`-член расчёта отходов
 *          (`wholeNewOffcut`, resultMetrics.ts), а НЕ опубликованная метрика #14
 *          `newOffcutArea` (та суммирует freeRects всех листов, вкл. `isOffcut`).
 *          Флаг `includeOffcutSheets` оставлен для полноты/тестов (дефолт false).
 *   §3.2 — агрегируем одинаковые `length × width × material` → одна строка с qty.
 *   §3.3 — `material` целого листа = `projectMaterial` (целые листы не несут
 *          `sheet.material`); `name` = `namePrefix`.
 * Порог годного остатка — `min(w, h) ≥ threshold` через тот же `isUsableOffcut`,
 * что и метрики раскроя (дефолт `DEFAULT_MIN_OFFCUT_MM`).
 *
 * Размер остатка из `Rect`: `length = r.w`, `width = r.h` (x — вдоль длины, y —
 * вдоль ширины; `cutting-engine/types.ts`).
 */

import type { OptimizationResult } from '@pomoshnik/shared';
import type { WarehouseOffcut } from '../model/types';
import { DEFAULT_MIN_OFFCUT_MM, isUsableOffcut } from './resultMetrics';

/** Запись склада без `id` (его проставит `WarehouseStore.add`). */
export type NewOffcut = Omit<WarehouseOffcut, 'id'>;

export interface CollectOffcutsOptions {
  /** Порог годного остатка по короткой стороне, мм (дефолт `DEFAULT_MIN_OFFCUT_MM`). */
  threshold?: number;
  /** Имя/партия записи склада (напр. «От Кухня Иванов»). */
  namePrefix?: string;
  /** Материал проекта (для целых листов, у которых нет `sheet.material`). */
  projectMaterial?: string;
  /** Агрегировать одинаковые length×width×material → qty (дефолт true). */
  aggregate?: boolean;
  /** Включать листы-остатки (`isOffcut`) — дефолт false (§3.1: только целые). */
  includeOffcutSheets?: boolean;
}

/**
 * Собрать новые годные остатки из результата раскроя. Возвращает массив записей
 * склада без `id` (готовы для `WarehouseStore.add`). Пустой результат / нет годных
 * freeRects → `[]`.
 */
export function collectNewOffcuts(
  result: OptimizationResult,
  opts: CollectOffcutsOptions = {},
): NewOffcut[] {
  const {
    threshold = DEFAULT_MIN_OFFCUT_MM,
    namePrefix = '',
    projectMaterial = '',
    aggregate = true,
    includeOffcutSheets = false,
  } = opts;

  const flat: NewOffcut[] = [];
  for (const sheet of result.sheets) {
    if (sheet.isOffcut && !includeOffcutSheets) continue;
    // Материал целого листа = материал проекта; листа-остатка — его собственный.
    const material = sheet.isOffcut ? (sheet.material ?? projectMaterial) : projectMaterial;
    for (const r of sheet.freeRects) {
      if (!isUsableOffcut(r, threshold)) continue;
      flat.push({ length: r.w, width: r.h, qty: 1, name: namePrefix, material });
    }
  }

  if (!aggregate) return flat;

  // Агрегация одинаковых length×width×material → суммарный qty (порядок первого вхождения).
  const byKey = new Map<string, NewOffcut>();
  for (const o of flat) {
    const key = `${o.length}×${o.width}×${o.material}`;
    const existing = byKey.get(key);
    if (existing) existing.qty += o.qty;
    else byKey.set(key, { ...o });
  }
  return [...byKey.values()];
}
